
import { Fragment, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Button } from './Button';

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string | ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'danger';
  isLoading?: boolean;
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  isLoading = false,
}: ConfirmDialogProps) {
  if (!isOpen) return null;

  return (
    <Fragment>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className={cn(
            'bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full',
            'transform transition-all',
            'animate-fade-in'
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby="dialog-title"
        >
          {/* Header */}
          <div className="px-6 pt-6 pb-4">
            <div className="flex items-start">
              {variant === 'danger' && (
                <div className="flex-shrink-0 mr-4">
                  <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-red-600 dark:text-red-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                </div>
              )}
              <div className="flex-1">
                <h3
                  id="dialog-title"
                  className={cn(
                    'text-lg font-semibold',
                    variant === 'danger'
                      ? 'text-red-800 dark:text-red-400'
                      : 'text-gray-900 dark:text-white'
                  )}
                >
                  {title}
                </h3>
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                  {message}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 rounded-b-lg flex items-center justify-end gap-3">
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={isLoading}
            >
              {cancelLabel}
            </Button>
            <Button
              variant={variant === 'danger' ? 'primary' : 'primary'}
              onClick={onConfirm}
              isLoading={isLoading}
              className={cn(
                variant === 'danger' &&
                  'bg-red-600 hover:bg-red-700 focus:ring-red-500'
              )}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </Fragment>
  );
}
